import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    approveToken,
    getShieldEstimate,
    getUnshieldEstimate,
    shieldTokens,
    unshieldTokens,
    clearShieldError,
    clearUnshieldError,
    clearShieldEstimate,
    clearUnshieldEstimate,
    resetApproveState,
    clearShieldTxHash,
    clearUnshieldTxHash
} from '../../../redux/slices/walletSlice'
import { getCoins } from '../../../redux/slices/coinsSlice'

export const useShieldFlow = (selectedToken, mode, wallet) => {
    const dispatch = useDispatch()
    const [step, setStep] = useState(1)
    const [amount, setAmount] = useState('')
    const [useMaxAmount, setUseMaxAmount] = useState(true)
    const [actualTxHash, setActualTxHash] = useState(null)
    const [showErrors, setShowErrors] = useState(false)

    const isShielding = mode === 'public'
    const actionText = isShielding ? 'Shield' : 'Unshield'

    const {
        shieldLoading,
        shieldError,
        shieldTxHash,
        unshieldLoading,
        unshieldError,
        unshieldTxHash,
        shieldEstimate,
        shieldEstimateLoading,
        shieldEstimateError,
        unshieldEstimate,
        unshieldEstimateLoading,
        unshieldEstimateError,
        approveLoading,
        approveError
    } = useSelector(state => state.wallet)

    // Initialize amount
    useEffect(() => {
        if (selectedToken && useMaxAmount) {
            setAmount(selectedToken.balance || '0')
        }
    }, [selectedToken, useMaxAmount])

    // Clear errors on mode change
    useEffect(() => {
        dispatch(clearShieldError())
        dispatch(clearUnshieldError())
        dispatch(clearShieldEstimate())
        dispatch(clearUnshieldEstimate())
        dispatch(resetApproveState())
        dispatch(clearShieldTxHash())
        dispatch(clearUnshieldTxHash())
    }, [dispatch, mode])

    // Fetch gas estimate on step 2
    useEffect(() => {
        if (selectedToken && amount && parseFloat(amount) > 0 && step === 2) {
            if (isShielding) {
                dispatch(getShieldEstimate({
                    tokenId: selectedToken.id,
                    amount
                }))
            } else {
                dispatch(getUnshieldEstimate({
                    tokenId: selectedToken.id,
                    amount
                }))
            }
        }
    }, [step, selectedToken, amount, isShielding, dispatch])

    // Auto-advance to success step
    useEffect(() => {
        if ((shieldTxHash !== undefined || unshieldTxHash !== undefined) && step === 3) {
            setActualTxHash(shieldTxHash || unshieldTxHash)
            setStep(4)
        }
    }, [shieldTxHash, unshieldTxHash, step])

    // Validation
    const isValidAmount = useCallback(() => {
        const numAmount = parseFloat(amount)
        const numBalance = parseFloat(selectedToken?.balance || 0)
        return amount && numAmount > 0 && numAmount <= numBalance
    }, [amount, selectedToken])

    const getAmountError = useCallback(() => {
        const numAmount = parseFloat(amount)
        const numBalance = parseFloat(selectedToken?.balance || 0)

        if (!amount || amount === "" || numAmount <= 0) return 'Amount is required'
        if (numAmount > numBalance) return 'Amount exceeds available balance'
        if (numAmount < 0) return 'Amount cannot be negative'
        return null
    }, [amount, selectedToken])

    // Handlers
    const handleAmountChange = useCallback((value) => {
        setAmount(value)
        setUseMaxAmount(false)
        if (showErrors) setShowErrors(false)
    }, [showErrors])

    const handleMaxClick = useCallback(() => {
        if (selectedToken?.balance) {
            setAmount(selectedToken.balance)
            setUseMaxAmount(true)
            if (showErrors) setShowErrors(false)
        }
    }, [selectedToken, showErrors])

    const handleApprove = useCallback(async () => {
        if (getAmountError()) {
            setShowErrors(true)
            return
        }
        setShowErrors(false)

        if (!isShielding) {
            setStep(2)
            return
        }

        if (!selectedToken?.id) return

        try {
            const result = await dispatch(approveToken({
                tokenId: selectedToken.id,
                amount
            }))

            if (approveToken.fulfilled.match(result)) {
                setStep(2)
            }
        } catch (error) {
            console.error('Token approval failed:', error)
        }
    }, [isShielding, selectedToken, amount, dispatch, getAmountError])

    const handleProceedToConfirm = useCallback(() => {
        setStep(3)
    }, [])

    const handleConfirm = useCallback(async () => {
        if (!selectedToken?.id) return

        // Build payload with gas parameters
        const payload = {
            tokenId: selectedToken.id,
            amount
        }

        // Add recipient address for unshield
        if (!isShielding && wallet?.publicWalletAddress) {
            payload.recipientAddress = wallet.publicWalletAddress
        }

        // Add gas parameters from estimate
        const estimate = isShielding ? shieldEstimate : unshieldEstimate
        if (estimate) {
            payload.gasLimit = estimate.gasEstimate
            payload.gasPrice = estimate.gasPrice
        }

        console.log('🚀 Final payload being sent:', payload)

        try {
            const action = isShielding ? shieldTokens : unshieldTokens
            const result = await dispatch(action(payload))

            if (action.fulfilled.match(result)) {
                const txHash = result.payload.data?.transactionHash || result.payload.data?.hash
                setActualTxHash(txHash)

                // Refetch coins after successful transaction
                // Shield: refetch public coins (source)
                // Unshield: refetch private coins (source)
                const modeToRefetch = isShielding ? 'public' : 'private'
                console.log(`✅ ${actionText} successful, refetching ${modeToRefetch} coins...`)
                dispatch(getCoins(modeToRefetch))

                // Immediately advance to success step
                setStep(4)
            }
        } catch (error) {
            console.error(`${actionText} operation failed:`, error)
        }
    }, [selectedToken, amount, isShielding, wallet, shieldEstimate, unshieldEstimate, actionText, dispatch])

    const handleBack = useCallback(() => {
        if (step > 1) setStep(step - 1)
    }, [step])

    const resetModal = useCallback(() => {
        setStep(1)
        setAmount('')
        setUseMaxAmount(true)
        setActualTxHash(null)
        dispatch(clearShieldError())
        dispatch(clearUnshieldError())
        dispatch(clearShieldEstimate())
        dispatch(clearUnshieldEstimate())
        dispatch(resetApproveState())
        dispatch(clearShieldTxHash())
        dispatch(clearUnshieldTxHash())
    }, [dispatch])

    return {
        // State
        step,
        amount,
        useMaxAmount,
        actualTxHash,
        isShielding,
        actionText,
        showErrors,
        setShowErrors,

        // Redux state
        shieldLoading,
        shieldError,
        shieldTxHash,
        unshieldLoading,
        unshieldError,
        unshieldTxHash,
        shieldEstimate,
        shieldEstimateLoading,
        shieldEstimateError,
        unshieldEstimate,
        unshieldEstimateLoading,
        unshieldEstimateError,
        approveLoading,
        approveError,

        // Validation
        isValidAmount,
        getAmountError,

        // Handlers
        handleAmountChange,
        handleMaxClick,
        handleApprove,
        handleProceedToConfirm,
        handleConfirm,
        handleBack,
        resetModal
    }
}
